import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  List,
  Button,
  Modal,
  message,
  Tooltip,
  Tag,
  Input,
  Icon,
  Empty,
  Dropdown,
  Menu,
  Popover
} from 'antd'
import {
  getAiVideos,
  deleteAiVideo,
  getAiVideoById,
  createAiVideo,
  updateAiVideo
} from '../../services/api/aiVideoGeneration'
import actions from '../../store/actions'
import { general } from '../../constants'
import './VideoList.scss'
import NewHeader from '../../components/NewHeader'
import AIVideoModal from './components/VideoGeneration/components/FinalVideo'
import StatusView from './components/StatusView/StatusView'
import moment from 'moment'
import VideoDuration from './components/VideoDuration'
import VideoAnalytics from './components/VideoAnalytics/VideoAnalytics'
import { ImagePreview } from '../../components'
import StoryboardDownloadModal from './components/StoryboardDownloadModal'
import { generateStoryboardPdf } from './utils/storyboardPdf'
import { generateStoryboardDocument } from './utils/storyboardDocument'
import { normalizeSceneAudioHistory } from './utils/audioHistory'

const STORYBOARD_DOCUMENT_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const parseJSONSafely = (val, fallback = null, maxDepth = 5) => {
  if (val === null || val === undefined || val === 'null') return fallback

  let result = val
  let depth = 0

  while (typeof result === 'string' && depth < maxDepth) {
    try {
      result = JSON.parse(result)
      depth++
    } catch {
      return depth > 0 ? result : fallback
    }
  }

  return result === null || result === undefined ? fallback : result
}

const stringifyJSONField = (val, fallback = null) => {
  if (val === null || val === undefined || val === 'null') {
    return fallback === null ? '' : JSON.stringify(fallback)
  }

  if (typeof val === 'string') {
    const parsed = parseJSONSafely(val, undefined)
    return parsed === undefined ? val : JSON.stringify(parsed)
  }

  return JSON.stringify(val)
}

const normalizeAudioMode = mode => {
  if (mode === 'advanced') return 'dialogue'

  if (
    mode === 'narration' ||
    mode === 'dialogue' ||
    mode === 'dialogue_and_narration'
  ) {
    return mode
  }

  return 'narration'
}

const getCreativeGuidelineSessionId = ({ video, creativeDirection }) =>
  creativeDirection?.sessionId ||
  creativeDirection?.session_id ||
  creativeDirection?.creative_guideline_session_id ||
  creativeDirection?.creativeGuidelineSessionId ||
  video.creative_guideline_session_id ||
  video.creativeGuidelineSessionId ||
  video.creative_direction_session_id ||
  null

const getClonePayloadFromVideo = video => {
  const videoCreationMode =
    video.video_creation_mode || video.videoCreationMode || null
  const creativeDirection = parseJSONSafely(
    video.creative_direction || video.creativeDirection,
    null
  )
  const creativeGuidelineSessionId = getCreativeGuidelineSessionId({
    video,
    creativeDirection
  })
  const creativeDirectionPayload = creativeDirection
    ? {
        ...creativeDirection,
        flowMode:
          videoCreationMode ||
          creativeDirection.flowMode ||
          creativeDirection.flow_mode ||
          null
      }
    : null

  return {
    voiceoverText: video.voiceover_text || video.voiceoverText || '',
    video_generation_mode:
      video.video_generation_mode || video.videoGenerationMode || 'manual',
    video_creation_mode: videoCreationMode,
    audio_mode: normalizeAudioMode(video.audio_mode || video.audioMode),
    voiceoverAudio: video.voiceover_audio || video.voiceoverAudio || null,
    videoStyle: video.video_style || video.videoStyle || null,
    creative_direction: creativeDirectionPayload
      ? JSON.stringify(creativeDirectionPayload)
      : '',
    creative_guideline_session_id: creativeGuidelineSessionId,
    shotGenerationStyle:
      video.shot_generation_style || video.shotGenerationStyle || null,
    editingStyle: video.editing_style || video.editingStyle || false,
    aspectRatio: video.aspect_ratio || video.aspectRatio || '16:9',
    selectedCharacter: parseJSONSafely(
      video.selected_character || video.selectedCharacter,
      []
    ),
    shotList: (video.shot_list || video.shotList || []).map(scene =>
      normalizeSceneAudioHistory({
        ...scene,
        shots: (scene.shots || []).map(shot => ({ ...shot }))
      })
    ),
    video_url: video.video_url || video.finalVideo || '',
    location_map: stringifyJSONField(
      video.location_map || video.locationMap,
      {}
    ),
    location_reference: stringifyJSONField(
      video.location_reference || video.locationReference,
      {}
    ),
    character_map: stringifyJSONField(
      video.character_map || video.characterMap,
      {}
    ),
    props_reference: stringifyJSONField(
      video.props_reference || video.prop_reference || video.propsReference,
      {}
    ),
    selected_voice: video.selected_voice || video.selectedVoice || null,
    lang: video.lang || video.language || video.lng || 'en',
    audio: video.audio || {},
    video: video.video || {},
    draft: video.draft || {},
    outfit_map: stringifyJSONField(video.outfit_map || video.outfitMap, {}),
    prop_map: stringifyJSONField(video.prop_map || video.propMap, {}),
    props: stringifyJSONField(video.props, {}),
    wardrobe: stringifyJSONField(video.wardrobe, {}),
    outfits_reference: stringifyJSONField(
      video.outfits_reference || video.outfitsReference,
      {}
    ),
    outfits: parseJSONSafely(video.outfits, []),
    final_videos_history: stringifyJSONField(video.final_videos_history, []),
    transitions: stringifyJSONField(video.transitions, []),
    character_voices: stringifyJSONField(
      video.character_voices || video.characterVoices,
      {}
    ),
    file: null
  }
}

const getVideoListItemFromCloneResponse = ({
  createdVideo,
  sourceVideo,
  payload,
  fallbackId
}) => ({
  ...sourceVideo,
  ...createdVideo,
  id: createdVideo?.id || fallbackId,
  voiceover_text:
    createdVideo?.voiceover_text ||
    sourceVideo?.voiceover_text ||
    payload.voiceoverText ||
    '',
  video_url:
    createdVideo?.video_url !== undefined
      ? createdVideo.video_url
      : payload.video_url || sourceVideo?.video_url || '',
  updated_at:
    createdVideo?.updated_at ||
    createdVideo?.created_at ||
    new Date().toISOString(),
  creator: createdVideo?.creator || sourceVideo?.creator,
  creator_id: createdVideo?.creator_id || sourceVideo?.creator_id,
  draft: createdVideo?.draft || sourceVideo?.draft,
  status: createdVideo?.status || sourceVideo?.status,
  video_creation_mode:
    createdVideo?.video_creation_mode ||
    payload.video_creation_mode ||
    sourceVideo?.video_creation_mode ||
    sourceVideo?.videoCreationMode ||
    null,
  creative_direction:
    createdVideo?.creative_direction ||
    payload.creative_direction ||
    sourceVideo?.creative_direction ||
    '',
  creative_guideline_session_id:
    createdVideo?.creative_guideline_session_id ||
    payload.creative_guideline_session_id ||
    sourceVideo?.creative_guideline_session_id ||
    sourceVideo?.creativeGuidelineSessionId ||
    null
})

const AI_VIDEO_STEPS = [
  { key: 'uploadDocument', title: 'Upload', icon: 'file-text' },
  { key: 'voiceover', title: 'Voiceover', icon: 'sound' },
  { key: 'videoStyle', title: 'Visual Setup', icon: 'camera' },
  { key: 'creativeDirection', title: 'AI Director', icon: 'compass' },
  { key: 'shotList', title: 'Shot List', icon: 'ordered-list' },
  { key: 'imageGeneration', title: 'Images', icon: 'picture' },
  { key: 'videoGeneration', title: 'Video', icon: 'video-camera' },
  { key: 'finalVideo', title: 'Final Result', icon: 'play-circle' }
]

const getVideoCreationMode = item =>
  item?.video_creation_mode || item?.videoCreationMode || 'basic'

const isAdvancedVideoCreationMode = item =>
  getVideoCreationMode(item) === 'advanced'

const getAiVideoStepsForItem = item =>
  AI_VIDEO_STEPS.filter(
    step => step.key !== 'creativeDirection' || isAdvancedVideoCreationMode(item)
  )

const resolveVoiceoverStepByAudioMode = item => {
  const mode = item?.audio_mode || item?.audioMode || 'narration'

  if (mode === 'dialogue') {
    return 'characterVoiceover'
  }

  return 'narrationVoiceover'
}

const resolveStepKey = (item, stepKey) => {
  if (stepKey === 'voiceover') {
    return resolveVoiceoverStepByAudioMode(item)
  }
  return stepKey
}

const VideoList = ({ onCreateNew, onEditVideo, onEditOnboarding }) => {
  const dispatch = useDispatch()
  const userInfo = useSelector(state => state.user.info)
  const isSuperAdmin = userInfo?.isSuperAdmin || userInfo?.is_super_admin

  const [videos, setVideos] = useState([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 12

  const [searchText, setSearchText] = useState('')
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [analyticsModalVisible, setAnalyticsModalVisible] = useState(false)
  const [analyzingVideo, setAnalyzingVideo] = useState(null)

  const [creatorModalVisible, setCreatorModalVisible] = useState(false)
  const [selectedCreator, setSelectedCreator] = useState(null)
  const [pdfModalVisible, setPdfModalVisible] = useState(false)
  const [pdfPreparing, setPdfPreparing] = useState(false)
  const [pdfProgress, setPdfProgress] = useState(0)
  const [pdfStatusText, setPdfStatusText] = useState('')
  const [pendingPdfItem, setPendingPdfItem] = useState(null)
  const [pdfQuality, setPdfQuality] = useState('medium')
  const [storyboardFormat, setStoryboardFormat] = useState('pdf')
  const [generatedStoryboardExports, setGeneratedStoryboardExports] = useState({
    pdf: null,
    document: null
  })
  const [isAddingToSop, setIsAddingToSop] = useState(false)
  const [isSopAdded, setIsSopAdded] = useState(false)
  const [cloneModalVisible, setCloneModalVisible] = useState(false)
  const [cloneStep, setCloneStep] = useState('confirm')
  const [cloneSourceVideo, setCloneSourceVideo] = useState(null)
  const [clonedVideoId, setClonedVideoId] = useState(null)
  const [cloneError, setCloneError] = useState('')
  const generatedStoryboardExport =
    generatedStoryboardExports[storyboardFormat] || null

  const handleCreatorClick = (e, creator) => {
    e.stopPropagation()
    if (creator) {
      setSelectedCreator(creator)
      setCreatorModalVisible(true)
    }
  }

  // Initial fetch or search change
  useEffect(() => {
    setVideos([])
    setOffset(0)
    setHasMore(true)
    fetchVideos(0, true)
  }, [searchText])

  const fetchVideos = async (currentOffset, isReset = false) => {
    setLoading(true)
    try {
      const response = await getAiVideos(currentOffset, PAGE_SIZE)

      if (response.data) {
        const videosFetched = response.data.videos || response.data || []

        setVideos(prev =>
          isReset ? videosFetched : [...prev, ...videosFetched]
        )

        // Update total if provided
        if (response.data.total !== undefined) {
          setTotal(response.data.total)
        }

        // Determine if there's more based on response length
        const moreAvailable = videosFetched.length === PAGE_SIZE
        setHasMore(moreAvailable)

        if (!isReset) {
          setOffset(currentOffset)
        }
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error)
      message.error('Failed to load videos')
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = () => {
    const nextOffset = offset + PAGE_SIZE
    fetchVideos(nextOffset)
  }

  const handleDelete = async videoId => {
    try {
      await deleteAiVideo(videoId)
      message.success('Video deleted successfully')
      // Refresh the current view
      setVideos([])
      setOffset(0)
      fetchVideos(0, true)
    } catch (error) {
      console.error('Failed to delete video:', error)
      message.error('Failed to delete video')
    }
  }

  const handlePreview = videoId => {
    const video = videos.find(video => video.id === videoId)
    setSelectedVideo(video)
    setPreviewModalVisible(true)
  }

  const handleOpenCloneModal = item => {
    setCloneSourceVideo(item)
    setClonedVideoId(null)
    setCloneError('')
    setCloneStep('confirm')
    setCloneModalVisible(true)
  }

  const handleCloseCloneModal = () => {
    if (cloneStep === 'cloning') return

    setCloneModalVisible(false)
    setCloneSourceVideo(null)
    setClonedVideoId(null)
    setCloneError('')
    setCloneStep('confirm')
  }

  const handleCloneVideo = async () => {
    if (!cloneSourceVideo?.id) return

    try {
      setCloneError('')
      setCloneStep('cloning')

      const sourceResponse = await getAiVideoById(cloneSourceVideo.id)
      const sourceVideo = sourceResponse?.data || cloneSourceVideo
      const payload = getClonePayloadFromVideo(sourceVideo)
      const createResponse = await createAiVideo({
        voiceoverText: payload.voiceoverText
      })
      const newVideoId = createResponse?.data?.id

      if (!newVideoId) {
        throw new Error('The clone was created without a video id.')
      }

      await updateAiVideo(newVideoId, payload)

      const clonedVideo = getVideoListItemFromCloneResponse({
        createdVideo: createResponse.data,
        sourceVideo,
        payload,
        fallbackId: newVideoId
      })

      setClonedVideoId(newVideoId)
      setCloneStep('success')
      message.success('Video cloned successfully')

      setVideos(prev => [
        clonedVideo,
        ...prev.filter(video => video.id !== newVideoId)
      ])
      setTotal(prev => (prev ? prev + 1 : prev))
    } catch (error) {
      console.error('Failed to clone video:', error)
      setCloneError(
        error?.message || 'Failed to clone the video. Please try again.'
      )
      setCloneStep('confirm')
      message.error('Failed to clone video')
    }
  }

  const createStoryboardExport = async item => {
    try {
      setPdfPreparing(true)
      const activeFormat = storyboardFormat
      const generator =
        activeFormat === 'document'
          ? generateStoryboardDocument
          : generateStoryboardPdf
      const generated = await generator({
        item,
        fetchVideoById: getAiVideoById,
        quality: pdfQuality,
        autoDownload: false,
        onProgress: ({ status, progress }) => {
          if (status) setPdfStatusText(status)
          if (typeof progress === 'number') setPdfProgress(progress)
        }
      })
      const generatedWithMeta = {
        ...generated,
        quality: pdfQuality,
        format: activeFormat
      }
      setGeneratedStoryboardExports(prev => ({
        ...prev,
        [activeFormat]: generatedWithMeta
      }))
      return generatedWithMeta
    } catch (error) {
      console.error('Failed to generate storyboard export:', error)
      message.error('Failed to prepare storyboard export. Please try again.')
      return null
    } finally {
      setPdfPreparing(false)
    }
  }

  const triggerStoryboardDownload = generated => {
    if (!generated?.blob || !generated?.fileName) return
    const url = URL.createObjectURL(generated.blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = generated.fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  const handleAddToSopDocuments = async generatedInput => {
    const fileData = generatedInput || generatedStoryboardExport
    const isDocumentExport =
      fileData?.format === 'document' ||
      fileData?.mimeType === STORYBOARD_DOCUMENT_MIME_TYPE ||
      /\.docx$/i.test(fileData?.fileName || '')
    if (!isDocumentExport) {
      message.warning(
        'Only storyboard documents can be added to SOP Documents.'
      )
      return
    }

    if (!fileData?.blob || !fileData?.fileName) {
      message.warning('Please generate the storyboard document first.')
      return
    }

    try {
      setIsAddingToSop(true)

      const file = new File([fileData.blob], fileData.fileName, {
        type: fileData.mimeType || STORYBOARD_DOCUMENT_MIME_TYPE
      })
      await dispatch(
        actions.library.upload(
          file,
          {
            parent_id: null,
            title: fileData.fileName,
            description: 'Storyboard document generated from AI Video'
          },
          general.ORGANIZATION_LIBRARY
        )
      )
      message.success('Storyboard added to SOP Documents successfully.')
      setIsSopAdded(true)
    } catch (error) {
      console.error('Failed to add storyboard to SOP Documents:', error)
      message.error('Failed to add storyboard to SOP Documents.')
    } finally {
      setIsAddingToSop(false)
    }
  }

  const handlePrimaryStoryboardAction = async () => {
    if (!pendingPdfItem || pdfPreparing) return

    let generated = generatedStoryboardExport
    if (
      !generated ||
      generated?.format !== storyboardFormat ||
      (storyboardFormat === 'pdf' && generated?.quality !== pdfQuality)
    ) {
      const created = await createStoryboardExport(pendingPdfItem)
      if (!created) return
      generated = created
      if (storyboardFormat === 'document') {
        setIsSopAdded(false)
      }
    }
  }

  const handleStoryboardDownload = () => {
    if (
      !generatedStoryboardExport?.blob ||
      !generatedStoryboardExport?.fileName
    ) {
      message.warning('Please prepare the storyboard export first.')
      return
    }
    triggerStoryboardDownload(generatedStoryboardExport)
  }

  const handleStoryboardAddToSop = async () => {
    if (
      !generatedStoryboardExport?.blob ||
      !generatedStoryboardExport?.fileName
    ) {
      message.warning('Please prepare the storyboard document first.')
      return
    }

    if (storyboardFormat === 'document') {
      await handleAddToSopDocuments(generatedStoryboardExport)
    }
  }

  const filteredVideos = useMemo(
    () =>
      searchText
        ? videos.filter(video => {
            return (
              video.voiceover_text &&
              video.voiceover_text
                .toLowerCase()
                .includes(searchText.toLowerCase())
            )
          })
        : videos,
    [videos, searchText]
  )
  const isSearchActive = searchText.trim().length > 0
  const visibleVideoCount = filteredVideos.length
  const shouldShowLoadMore =
    hasMore && (!isSearchActive || visibleVideoCount >= PAGE_SIZE)

  const renderVideoRow = item => {
    const draftStatus = item.draft?.status
    const draftId = item.draft?.id
    const availableSteps = getAiVideoStepsForItem(item)

    const isReady = !!item.video_url
    const isProcessing =
      draftId &&
      !isReady &&
      (draftStatus === 'pending' || item.status === 'processing')
    const isFailed =
      !isReady && (draftStatus === 'failed' || item.status === 'failed')

    // Status mapping
    let statusLabel = draftId ? 'Draft' : 'Manual'
    let statusColor = 'default'
    if (isReady) {
      statusLabel = 'Ready'
      statusColor = 'blue'
    } else if (isProcessing) {
      statusLabel = 'Processing'
      statusColor = 'orange'
    } else if (isFailed) {
      statusLabel = 'Failed'
      statusColor = 'red'
    }

    return (
      <List.Item key={item.id} className="video-row-item">
        <div className={`video-row ${isFailed ? 'video-row--failed' : ''}`}>
          <div
            className="video-row__preview"
            onClick={() => isReady && handlePreview(item.id)}
          >
            {isReady ? (
              <div className="video-thumbnail-wrapper">
                <video
                  src={`${item.video_url}#t=0.1`}
                  preload="metadata"
                  className="video-thumbnail"
                  playsInline
                  muted
                />
                <div className="play-icon-overlay">
                  <Icon type="play-circle" theme="filled" />
                  <span className="duration-pill">
                    <VideoDuration
                      videoUrl={item.video_url}
                      initialDuration={item.video_duration}
                    />
                  </span>
                </div>
              </div>
            ) : (
              <div
                className={`processing-state ${
                  isFailed
                    ? 'failed-state'
                    : isProcessing
                    ? 'pending-state'
                    : 'draft-state'
                }`}
              >
                <Icon
                  type={
                    isFailed
                      ? 'close-circle'
                      : isProcessing
                      ? 'loading'
                      : 'edit'
                  }
                />
                <span>
                  {isFailed
                    ? 'Failed'
                    : isProcessing
                    ? 'Processing'
                    : draftId
                    ? 'Draft'
                    : 'Unfinished'}
                </span>
              </div>
            )}
          </div>

          <div className="video-row__content">
            <div className="video-row__header">
              <Popover
                title={item.voiceover_text}
                placement="topLeft"
                trigger="click"
              >
                <div className="video-row__title-container">
                  <h4 className="video-row__title">
                    {item.voiceover_text?.length > 100
                      ? item.voiceover_text?.slice(0, 100) + '...'
                      : item.voiceover_text || 'Untitled Video'}
                  </h4>
                </div>
              </Popover>
            </div>

            <div className="video-row__meta">
              <div className="video-row__author">
                <div className="author-avatar" style={{ overflow: 'hidden' }}>
                  {item.creator?.avatar?.thumbnail ||
                  item.creator?.avatar?.link ? (
                    <img
                      src={
                        item.creator.avatar.thumbnail ||
                        item.creator.avatar.link
                      }
                      alt="creator"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Icon type="user" />
                  )}
                </div>
                {item.creator ? (
                  <span
                    className="creator-link"
                    onClick={e => handleCreatorClick(e, item.creator)}
                  >
                    {item.creator.name ||
                      (item.creator.first_name && item.creator.last_name
                        ? `${item.creator.first_name} ${item.creator.last_name}`
                        : null) ||
                      item.creator.first_name ||
                      item.creator.email}
                  </span>
                ) : (
                  <span>AI Video</span>
                )}
                <span className="meta-dot">•</span>
                <span className="meta-date">
                  {moment(item.updated_at).fromNow()}
                </span>
              </div>
              <Tag
                color={statusColor}
                className={`video-row__status video-row__status--${statusLabel.toLowerCase()}`}
              >
                <div className="status-dot" />
                {statusLabel}
              </Tag>
            </div>
          </div>

          <div className="video-row__actions">
            <div className="action-buttons-group">
              <div className="action-slot">
                <Tooltip title="Edit Video">
                  <Button
                    className="video-row__action-btn"
                    icon="edit"
                    type="link"
                    onClick={e => {
                      e.stopPropagation()
                      onEditVideo(item)
                    }}
                  />
                </Tooltip>
              </div>

              {isReady ? (
                <div className="action-slot">
                  <Dropdown
                    overlay={
                      <Menu>
                        {availableSteps.map(step => (
                          <Menu.Item
                            key={step.key}
                            onClick={e => {
                              onEditVideo(item, resolveStepKey(item, step.key))
                            }}
                          >
                            <Icon type={step.icon} /> {step.title}
                          </Menu.Item>
                        ))}
                      </Menu>
                    }
                    placement="bottomRight"
                    trigger={['hover']}
                  >
                    <Tooltip title="Jump to Step">
                      <Button
                        className="video-row__action-btn"
                        icon="fast-forward"
                        type="link"
                        onClick={e => {
                          e.stopPropagation()
                          onEditVideo(item, 'finalVideo')
                        }}
                      />
                    </Tooltip>
                  </Dropdown>
                </div>
              ) : (
                <div className="action-slot">
                  <Tooltip title="Jump to Step (available when ready)">
                    <div
                      className="video-row__action-btn video-row__action-btn--disabled"
                      onClick={e => e.stopPropagation()}
                    >
                      <Icon type="fast-forward" />
                    </div>
                  </Tooltip>
                </div>
              )}

              <div className="action-slot">
                <Dropdown
                  trigger={['click']}
                  placement="bottomRight"
                  overlay={
                    <Menu
                      onClick={({ key, domEvent }) => {
                        domEvent.stopPropagation()
                        if (key === 'preview' && isReady) {
                          handlePreview(item.id)
                          return
                        }
                        if (key === 'analytics') {
                          setAnalyzingVideo(item)
                          setAnalyticsModalVisible(true)
                          return
                        }
                        if (key === 'pdf' && isReady) {
                          setPendingPdfItem(item)
                          setGeneratedStoryboardExports({
                            pdf: null,
                            document: null
                          })
                          setIsSopAdded(false)
                          setStoryboardFormat('pdf')
                          setPdfProgress(0)
                          setPdfStatusText(
                            'This will prepare a storyboard export for this video.'
                          )
                          setPdfModalVisible(true)
                          return
                        }
                        if (key === 'clone') {
                          handleOpenCloneModal(item)
                          return
                        }
                        if (key === 'delete') {
                          Modal.confirm({
                            title: 'Delete this video?',
                            content: 'This action cannot be undone.',
                            okText: 'Delete',
                            okType: 'danger',
                            cancelText: 'Cancel',
                            onOk: () => handleDelete(item.id)
                          })
                        }
                      }}
                    >
                      {isReady && (
                        <Menu.Item key="preview">
                          <Icon type="play-circle" /> Preview
                        </Menu.Item>
                      )}
                      {isReady && (
                        <Menu.Item key="pdf">
                          <Icon type="download" /> Export Storyboard
                        </Menu.Item>
                      )}
                      {isSuperAdmin && (
                        <Menu.Item key="analytics">
                          <Icon type="line-chart" /> Analytics
                        </Menu.Item>
                      )}
                      <Menu.Item key="clone">
                        <Icon type="copy" /> Clone Video
                      </Menu.Item>
                      <Menu.Divider />
                      <Menu.Item key="delete" style={{ color: '#ff4d4f' }}>
                        <Icon type="delete" /> Delete
                      </Menu.Item>
                    </Menu>
                  }
                >
                  <Tooltip title="More Actions">
                    <Button
                      className="video-row__action-btn"
                      icon="ellipsis"
                      type="link"
                      onClick={e => e.stopPropagation()}
                    />
                  </Tooltip>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </List.Item>
    )
  }

  return (
    <div className="video-list-page">
      <div className="video-list-page__header">
        <div className="video-list-page__header-content">
          <div className="video-list-page__title-area">
            <h1>AI Video Studio</h1>
            <p>Create and manage your AI-generated video content</p>
          </div>
          <div className="video-list-page__actions">
            <NewHeader />
          </div>
        </div>

        <div className="video-list-page__filters">
          <Input
            prefix={<Icon type="search" style={{ color: 'rgba(0,0,0,.25)' }} />}
            placeholder="Search your videos..."
            className="video-list-search-input"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
          <div className="video-list-page__filter-actions">
            {onEditOnboarding ? (
              <Button
                icon="edit"
                className="btn-edit-onboarding"
                onClick={onEditOnboarding}
              >
                Edit onboarding
              </Button>
            ) : null}
            <Button
              type="primary"
              icon="plus"
              className="btn-create"
              onClick={onCreateNew}
            >
              Create New Video
            </Button>
          </div>
        </div>
      </div>

      <div className="video-list-page__content">
        {loading && !videos.length ? (
          <StatusView
            title="Loading Videos"
            subTitle="Fetching your creative content..."
          />
        ) : (
          <div className="video-list-container">
            <List
              loading={loading && videos.length === 0}
              dataSource={filteredVideos}
              renderItem={renderVideoRow}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <div className="empty-state">
                        <h3>No videos found</h3>
                        <p>Get started by creating your first AI video</p>
                        <Button type="primary" onClick={onCreateNew}>
                          Create Video
                        </Button>
                      </div>
                    }
                  />
                )
              }}
            />
            {videos.length > 0 && (!isSearchActive || visibleVideoCount > 0) && (
              <div className="video-list-footer">
                <div className="video-list-status">
                  Showing <strong>{visibleVideoCount}</strong>
                  {total > 0 && (
                    <span>
                      {' '}
                      of <strong>{total}</strong>
                    </span>
                  )}{' '}
                  videos
                </div>

                {shouldShowLoadMore && (
                  <div className="video-list-load-more">
                    <Button
                      loading={loading}
                      onClick={handleLoadMore}
                      className="btn-load-more"
                      icon={loading ? undefined : 'down'}
                    >
                      {loading
                        ? isSearchActive
                          ? 'Fetching more source videos...'
                          : `Fetching next ${PAGE_SIZE} videos...`
                        : isSearchActive
                        ? 'Load more source videos'
                        : `Load ${PAGE_SIZE} more videos`}
                    </Button>
                  </div>
                )}

                {!hasMore && (
                  <div className="video-list-end">
                    <Icon
                      type="check-circle"
                      theme="twoTone"
                      twoToneColor="#52c41a"
                    />
                    <span>You've explored all your cinematic creations</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {selectedVideo && selectedVideo.video_url && (
        <AIVideoModal
          visible={true}
          videoUrl={selectedVideo.video_url}
          title={'Your AI Generated Video'}
          onClose={() => setSelectedVideo({})}
          onEdit={step => onEditVideo(selectedVideo, step)}
        />
      )}

      <VideoAnalytics
        visible={analyticsModalVisible}
        onClose={() => {
          setAnalyticsModalVisible(false)
          setAnalyzingVideo(null)
        }}
        videoId={analyzingVideo?.id}
        userId={analyzingVideo?.creator_id}
        videoTitle={analyzingVideo?.voiceover_text}
      />

      <StoryboardDownloadModal
        visible={pdfModalVisible}
        preparing={pdfPreparing}
        progress={pdfProgress}
        statusText={pdfStatusText}
        quality={pdfQuality}
        format={storyboardFormat}
        canStart={!!pendingPdfItem}
        sopAdded={isSopAdded}
        onQualityChange={value => {
          setPdfQuality(value)
          setGeneratedStoryboardExports(prev => ({
            ...prev,
            pdf: null
          }))
        }}
        onFormatChange={value => {
          setStoryboardFormat(value)
          setPdfProgress(0)
          setPdfStatusText(
            value === 'document'
              ? 'This will prepare an editable storyboard document.'
              : 'This will prepare a storyboard PDF.'
          )
        }}
        onCancel={() => {
          if (!pdfPreparing) {
            setPdfModalVisible(false)
            setPendingPdfItem(null)
            setGeneratedStoryboardExports({
              pdf: null,
              document: null
            })
            setIsSopAdded(false)
            setStoryboardFormat('pdf')
          }
        }}
        onStart={() => {
          handlePrimaryStoryboardAction()
        }}
        onDownload={handleStoryboardDownload}
        onAddToSop={handleStoryboardAddToSop}
        addingToSop={isAddingToSop}
        isGenerated={!!generatedStoryboardExport}
        generatedFileName={generatedStoryboardExport?.fileName || ''}
      />

      <Modal
        centered
        maskClosable={cloneStep !== 'cloning'}
        closable={cloneStep !== 'cloning'}
        visible={cloneModalVisible}
        title={
          cloneStep === 'success' ? 'Video Cloned Successfully' : 'Clone Video'
        }
        onCancel={handleCloseCloneModal}
        footer={
          cloneStep === 'success'
            ? [
                <Button key="stay" onClick={handleCloseCloneModal}>
                  Stay on List
                </Button>,
                <Button
                  key="edit"
                  type="primary"
                  icon="edit"
                  onClick={() =>
                    onEditVideo({ id: clonedVideoId }, 'uploadDocument')
                  }
                >
                  Edit Cloned Video
                </Button>
              ]
            : [
                <Button
                  key="cancel"
                  onClick={handleCloseCloneModal}
                  disabled={cloneStep === 'cloning'}
                >
                  Cancel
                </Button>,
                <Button
                  key="clone"
                  type="primary"
                  icon="copy"
                  loading={cloneStep === 'cloning'}
                  onClick={handleCloneVideo}
                >
                  {cloneStep === 'cloning' ? 'Cloning...' : 'Clone Video'}
                </Button>
              ]
        }
      >
        {cloneStep === 'success' ? (
          <div className="clone-video-modal__content">
            <Icon
              type="check-circle"
              theme="twoTone"
              twoToneColor="#52c41a"
              className="clone-video-modal__icon"
            />
            <div>
              <p className="clone-video-modal__title">
                The cloned video is ready.
              </p>
              <p className="clone-video-modal__description">
                You can continue browsing the list or open the cloned video to
                edit it.
              </p>
            </div>
          </div>
        ) : (
          <div className="clone-video-modal__content">
            <Icon
              type={cloneStep === 'cloning' ? 'loading' : 'copy'}
              className="clone-video-modal__icon"
            />
            <div>
              <p className="clone-video-modal__title">
                Create a full copy of this video?
              </p>
              <p className="clone-video-modal__description">
                The new video will keep the same story, shot list, generated
                assets, voice settings, and final video. The original video will
                stay unchanged.
              </p>
              {cloneError && (
                <p className="clone-video-modal__error">{cloneError}</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        centered
        maskClosable={false}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: '#e6f7ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#1890ff',
                overflow: 'hidden'
              }}
            >
              {selectedCreator?.avatar?.thumbnail ||
              selectedCreator?.avatar?.link ? (
                <ImagePreview
                  src={
                    selectedCreator.avatar.thumbnail ||
                    selectedCreator.avatar.link
                  }
                  alt="creator profile"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Icon type="user" />
              )}
            </div>
            <span style={{ fontSize: '16px', fontWeight: 500 }}>
              Creator Profile
            </span>
          </div>
        }
        visible={creatorModalVisible}
        centered
        width={400}
        onCancel={() => {
          setCreatorModalVisible(false)
          setSelectedCreator(null)
        }}
        footer={null}
        bodyStyle={{ paddingTop: 0 }}
      >
        {selectedCreator && (
          <div className="creator-details" style={{ padding: '8px 4px' }}>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Full Name
                </div>
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#262626'
                  }}
                >
                  {selectedCreator.name ||
                    (selectedCreator.first_name && selectedCreator.last_name
                      ? `${selectedCreator.first_name} ${selectedCreator.last_name}`
                      : null) ||
                    selectedCreator.first_name ||
                    'N/A'}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}
                >
                  Email Address
                </div>
                <div style={{ fontSize: '15px', color: '#262626' }}>
                  {selectedCreator.email || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default VideoList
